import React, { useState } from 'react';
import { Box, Button, Popover, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { RX_STATUS_WORD_LABELS, type RxStatusWord } from '../../../../../patient/rxStatusWords';
import { DISPENSE_STATUS_LABELS } from './RxStatusCell';

/**
 * PRX-137 — what the status chips on the Prescriptions tab mean, per PMS.
 *
 * Fillware's letters, as measured live (`backend-v2/src/common/fillware/rx-gate.ts`):
 * D deleted, T transferred out, F transferred IN, I inactive, blank active,
 * and Hold is a separate flag. There is no U, and F is not "filled". Rafik's
 * wireframe legend (F/H/U/D/I) is Kroll-shaped and is not copied here. The
 * words come from the one shared catalogue (`patient/rxStatusWords.ts`).
 */
export const FILLWARE_STATUS_LEGEND: ReadonlyArray<{ code: string; word: RxStatusWord }> = [
  { code: 'Blank', word: 'active' },
  { code: 'Hold flag', word: 'hold' },
  { code: 'F', word: 'transferred_in' },
  { code: 'T', word: 'transferred_out' },
  { code: 'I', word: 'inactive' },
  { code: 'D', word: 'deleted' },
];

export interface RxStatusLegendProps {
  databaseType: string | null;
}

const LegendRows: React.FC<{ rows: Array<{ code: string; label: string }> }> = ({ rows }) => (
  <Table size="small">
    <TableBody>
      {rows.map((row) => (
        <TableRow key={row.code}>
          <TableCell sx={{ fontFamily: 'monospace', width: 96, color: 'text.secondary' }}>{row.code}</TableCell>
          <TableCell>{row.label}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

export const RxStatusLegend: React.FC<RxStatusLegendProps> = ({ databaseType }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (databaseType !== 'fillware' && databaseType !== 'kroll') return null;

  return (
    <>
      <Button
        size="small"
        startIcon={<InfoOutlinedIcon fontSize="small" />}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchor)}
      >
        Status legend
      </Button>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { p: 1.5, maxWidth: 360 },
            ...({ role: 'dialog', 'aria-label': 'Status legend' } as React.HTMLAttributes<HTMLDivElement>),
          },
        }}
      >
        {databaseType === 'fillware' ? (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Fillware status
            </Typography>
            <LegendRows
              rows={FILLWARE_STATUS_LEGEND.map((e) => ({ code: e.code, label: RX_STATUS_WORD_LABELS[e.word] }))}
            />
          </Box>
        ) : (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Rx active
            </Typography>
            <LegendRows
              rows={[
                { code: 'Active', label: 'The Rx can be filled' },
                { code: 'Inactive', label: 'The Rx is inactivated in Kroll' },
              ]}
            />
            <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5 }}>
              Dispense status
            </Typography>
            <LegendRows
              rows={Object.entries(DISPENSE_STATUS_LABELS).map(([code, label]) => ({ code, label }))}
            />
          </Box>
        )}
      </Popover>
    </>
  );
};

export default RxStatusLegend;
